import { CSSProperties, FC, useState, useContext, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Ring } from "@uiball/loaders";
import ReactMarkdown from "react-markdown";
import { InstanceContext } from "../../context/InstanceContext";
import { getAuthInfoFromLocalStorage } from "../../context/AuthContext";
import { UIConfigContext } from "../../context/UIConfigContext";
import { sharedMarkdownComponents } from "../../components/Markdown/markdownConfig";
import {
  quoteSeatsAsync,
  checkoutSeatAsync,
  formatSeatAmount,
  ISeatQuote,
  SeatInterval,
} from "../../services/crystal";
import { ICrystalListMarker, ICrystalNotice } from "../../data/interface";
import ComparisonTable from "../../components/Crystal/ComparisonTable";
import FeatureSlideshow from "../../components/Crystal/FeatureSlideshow";
import HardwareRequirements from "../../components/Crystal/HardwareRequirements";
import {
  goldPlusVsCrystalColumns,
  goldPlusVsCrystalRows,
  msp30DaysVsBasicColumns,
  msp30DaysVsBasicRows,
} from "../../data/crystalPageContent";
import { Environment, getEnv } from "../../utils";
import styles from "./index.module.css";

const IMG = "/data/crystal";

type Interval = SeatInterval;

// Display order (shortest to longest commitment)/labels for whatever
// intervals the quote actually returns prices for (a seat_type isn't
// guaranteed to offer all of these - e.g. not every plan has a three_year
// price).
const INTERVAL_ORDER: Interval[] = ["monthly", "yearly", "three_year"];
const DEFAULT_INTERVAL: Interval = "monthly";
const INTERVAL_LABELS: Record<Interval, string> = {
  yearly: "Yearly",
  monthly: "Monthly",
  three_year: "3-Year",
};
// Suffix for the "renews automatically at $X{suffix}" trial disclaimer.
const INTERVAL_SUFFIXES: Record<Interval, string> = {
  yearly: "/year",
  monthly: "/month",
  three_year: "/3 years",
};

// Friendly labels for known seat types; falls back to the raw seat_type.
const DEFAULT_SEAT_TYPE = "crystal_normal_tier1";
const SEAT_TYPE_LABELS: Record<string, string> = {
  crystal_normal_tier1: "Crystal SE + MSP Basic",
  crystal_normal_tier2: "Crystal SE + MSP 30-Day Flows",
};

function seatTypeLabel(seatType: string): string {
  return SEAT_TYPE_LABELS[seatType] ?? seatType;
}

// The multi-MSP-instance selector has no real customers in production (an
// owner never has more than one instance there); keep it around for QA but
// hide it everywhere public-facing, including beta. An explicit allow-list
// (rather than `!== OFFICIAL`) so a future Environment value defaults to
// hidden instead of silently becoming visible.
const SHOW_INSTANCE_SELECTOR =
  getEnv() === Environment.LOCAL || getEnv() === Environment.DEV;

const LIST_MARKER_CLASS: Record<ICrystalListMarker, string> = {
  dot: styles["order-notices-list-dot"],
  dash: styles["order-notices-list-dash"],
  number: styles["order-notices-list-number"],
};

function parseListMarker(value: unknown): ICrystalListMarker | undefined {
  if (value === "dot" || value === "dash" || value === "number") return value;
  if (value === "bullet") return "dot";
  if (value === "numbered") return "number";
  return undefined;
}

function parseNotice(raw: unknown): ICrystalNotice | null {
  if (typeof raw === "string") {
    return raw ? { type: "text", content: raw } : null;
  }
  if (!raw || typeof raw !== "object") return null;
  const block = raw as {
    type?: unknown;
    content?: unknown;
    items?: unknown;
    marker?: unknown;
    notices_type?: unknown;
    marker_color?: unknown;
  };
  if (block.type === "list" && Array.isArray(block.items)) {
    const items = block.items.filter((item): item is string => typeof item === "string");
    if (!items.length) return null;
    return {
      type: "list",
      items,
      marker: parseListMarker(block.marker) ?? parseListMarker(block.notices_type) ?? "dot",
      marker_color: typeof block.marker_color === "string" ? block.marker_color : undefined,
    };
  }
  const content =
    typeof block.content === "string"
      ? block.content
      : Array.isArray(block.items)
        ? block.items.filter((item): item is string => typeof item === "string").join("\n\n")
        : "";
  return content ? { type: "text", content } : null;
}

const NoticeList: FC<{ notice: Extract<ICrystalNotice, { type: "list" }> }> = ({ notice }) => {
  const marker: ICrystalListMarker = notice.marker ?? "dot";
  const ListTag = marker === "number" ? "ol" : "ul";
  return (
    <ListTag
      className={`${styles["order-notices-list"]} ${LIST_MARKER_CLASS[marker]}`}
      style={{ "--notice-marker-color": notice.marker_color || "#027bff" } as CSSProperties}
    >
      {notice.items.map((item, j) => (
        <li key={j}>
          <ReactMarkdown components={sharedMarkdownComponents}>{item}</ReactMarkdown>
        </li>
      ))}
    </ListTag>
  );
};

const Crystal: FC = () => {
  const { instancesWithRole } = useContext(InstanceContext);
  const { config } = useContext(UIConfigContext);
  const orderConfig = config?.crystal_order_config;

  // deep-link support: an external link can pass ?type=<seat_type>&subscription=<monthly|yearly>
  // to pre-fill the Crystal Type / Billing Cycle selectors.
  const [searchParams] = useSearchParams();
  const requestedSeatType = searchParams.get("type");
  const requestedInterval = searchParams.get("subscription");

  // A crystal seat binds to an MSP instance the customer owns; members can't buy.
  const ownerInstances = useMemo(
    () => instancesWithRole.filter((it) => it.role === "owner"),
    [instancesWithRole]
  );

  // Default to the sole owned instance; when several exist the customer picks.
  const [mspId, setMspId] = useState("");
  useEffect(() => {
    setMspId((prev) =>
      ownerInstances.some((it) => it.id === prev)
        ? prev
        : ownerInstances[0]?.id ?? ""
    );
  }, [ownerInstances]);

  const [seats, setSeats] = useState<ISeatQuote[]>([]);
  const [outOfStock, setOutOfStock] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(true);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  const [seatType, setSeatType] = useState("");
  const [interval, setInterval] = useState<Interval>(
    INTERVAL_ORDER.includes(requestedInterval as Interval)
      ? (requestedInterval as Interval)
      : DEFAULT_INTERVAL
  );

  const [submitting, setSubmitting] = useState(false);

  // load the price list. Quote is public; checkout still requires a session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setQuoteError(null);
        const { seats, out_of_stock } = await quoteSeatsAsync();
        if (cancelled) return;
        const usable = seats.filter((s) => s.prices?.length);
        setSeats(usable);
        setOutOfStock(!!out_of_stock);
        setSeatType((prev) => {
          if (usable.some((s) => s.seat_type === prev)) return prev;
          if (usable.some((s) => s.seat_type === requestedSeatType)) {
            return requestedSeatType as string;
          }
          if (usable.some((s) => s.seat_type === DEFAULT_SEAT_TYPE)) {
            return DEFAULT_SEAT_TYPE;
          }
          return usable[0]?.seat_type ?? "";
        });
      } catch (err) {
        if (!cancelled) {
          setQuoteError(
            (err as Error).message || "Failed to load Crystal plans."
          );
        }
      } finally {
        if (!cancelled) setLoadingQuote(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedSeat = useMemo(
    () => seats.find((s) => s.seat_type === seatType),
    [seats, seatType]
  );

  // Not every seat_type offers the same intervals (e.g. a plan may skip
  // three_year) - if switching Crystal Type drops the currently-selected
  // one, fall back to the first the new seat actually has, in INTERVAL_ORDER.
  useEffect(() => {
    if (!selectedSeat) return;
    setInterval((prev) => {
      if (selectedSeat.prices.some((p) => p.interval === prev)) return prev;
      const available = INTERVAL_ORDER.filter((i) =>
        selectedSeat.prices.some((p) => p.interval === i)
      );
      return available[0] ?? prev;
    });
  }, [selectedSeat]);

  // Figma shows just the amount here (e.g. "$99.99"); the trial disclaimer
  // has its own line below the Order Now button.
  const priceLabel = useMemo(() => {
    if (!selectedSeat) return "";
    const price = selectedSeat.prices.find((p) => p.interval === interval);
    if (!price) return "Not available for this billing period";
    return formatSeatAmount(price.amount, price.currency);
  }, [selectedSeat, interval]);

  // send the customer to Stripe. Keep the URLs stable (no cache-busting) so a
  // double-submit reuses the open checkout session rather than replacing it.
  async function proceedToCheckout(seat_type: string, billingInterval: Interval) {
    if (submitting) return;
    try {
      setSubmitting(true);
      const { checkout_url } = await checkoutSeatAsync({
        seat_type,
        interval: billingInterval,
        // No owned instance to bind to: omit msp_id so the backend provisions one.
        ...(mspId ? { msp_id: mspId } : {}),
        success_url: `${window.location.origin}/crystal/success`,
        cancel_url: `${window.location.origin}/crystal`,
      });
      window.location.replace(checkout_url);
    } catch (err) {
      console.error(err);
      // request() already sent the user to login when there is no session
      if (getAuthInfoFromLocalStorage()?.id_token) {
        alert(err);
      }
      setSubmitting(false);
    }
  }

  function handleOrder() {
    proceedToCheckout(seatType, interval);
  }

  const priceUnavailable =
    !!selectedSeat && !selectedSeat.prices.find((p) => p.interval === interval);
  // Only an explicit `false` disables ordering from config - omitted/undefined
  // stays enabled so existing config payloads aren't broken by this field.
  const orderButtonEnabled = orderConfig?.order_button_enabled !== false;
  const orderDisabled =
    submitting ||
    loadingQuote ||
    !seatType ||
    outOfStock ||
    priceUnavailable ||
    !orderButtonEnabled;

  // Plain string-replace the {{placeholders}} before handing to react-markdown -
  // price/interval_suffix are the only substitutions this template supports.
  // Any link (e.g. "See full terms") is already written into the template
  // itself as Markdown, so it renders inline as part of the same paragraph.
  const trialTermsMarkdown = useMemo(() => {
    const template = orderConfig?.trial_terms_template;
    if (!selectedSeat || typeof template !== "string" || !template || priceUnavailable) return "";
    return template
      .replace(/{{\s*price\s*}}/g, priceLabel)
      .replace(/{{\s*interval_suffix\s*}}/g, INTERVAL_SUFFIXES[interval]);
  }, [selectedSeat, orderConfig, priceLabel, interval, priceUnavailable]);

  const notices = orderConfig?.notices;
  const structuredNotices = useMemo(() => {
    if (!Array.isArray(notices) || !notices.length) return null;
    const raw: unknown[] = notices;
    if (typeof raw[0] === "string") {
      const items = raw.filter((item): item is string => typeof item === "string" && !!item);
      if (!items.length) return null;
      if (orderConfig?.notices_type === "plain") {
        return items.map((content) => ({ type: "text" as const, content }));
      }
      return [
        {
          type: "list" as const,
          items,
          marker: parseListMarker(orderConfig?.notices_type) ?? "dot",
        },
      ];
    }
    const parsed = raw.map(parseNotice).filter((n): n is ICrystalNotice => n !== null);
    return parsed.length ? parsed : null;
  }, [notices, orderConfig?.notices_type]);

  if (loadingQuote) {
    return (
      <div className={styles["page"]} style={{ alignItems: "center", justifyContent: "center" }}>
        <Ring size={60} />
      </div>
    );
  }

  return (
    <div className={styles["page"]}>
      {/* ---------------------------------------------------------------- */}
      {/* Hero + order box                                                 */}
      {/* ---------------------------------------------------------------- */}
      <section className={styles["hero"]}>
        <img
          src={`${IMG}/crystal-title.png`}
          alt="Firewalla Crystal. Awesome Software Experience on your own hardware (or virtual machine)."
          style={{ width: "100%", maxWidth: "660px", marginBottom: "64px" }}
        />

        <div className={styles["order-box"]}>
          <div className={styles["crystal-se-box"]}>
            <h2 className={styles["crystal-se-box-title"]}>Crystal SE</h2>
            <div className={styles["crystal-se-box-ports"]}>
              <img src={`${IMG}/order-box-port-icon.svg`} alt="" className={styles["crystal-se-box-port-icon"]} />
              <img src={`${IMG}/order-box-port-icon.svg`} alt="" className={styles["crystal-se-box-port-icon"]} />
            </div>
            <p className={styles["crystal-se-box-desc"]}>
              <strong>Up to 2 Ports</strong>: 1 WAN + 1 LAN
              <br />
              (additional ports bridged into the LAN)
            </p>
          </div>
          <div className={styles["crystal-se-caption"]}>
            All the same features as <strong>Firewalla Gold</strong>
          </div>

          {SHOW_INSTANCE_SELECTOR && ownerInstances.length > 1 && (
            <div className={styles["instance-selector"]}>
              <div className={styles["row"]}>
                <label className={styles["row-label"]} htmlFor="msp-instance">
                  MSP Instance:
                </label>
                <select
                  id="msp-instance"
                  className={styles["select"]}
                  value={mspId}
                  onChange={(e) => setMspId(e.target.value)}
                >
                  {ownerInstances.map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.name || it.domain}
                    </option>
                  ))}
                </select>
              </div>
              <p className={styles["instance-selector-note"]}>
                Only shown in test environments (not beta or production), for owners
                of multiple MSP instances.
              </p>
            </div>
          )}

          <div className={styles["row-pair"]}>
            <div className={styles["row"]}>
              <label className={styles["row-label"]} htmlFor="flow-storage">
                Crystal Type
              </label>
              <select
                id="flow-storage"
                className={styles["select"]}
                value={seatType}
                disabled={loadingQuote}
                onChange={(e) => setSeatType(e.target.value)}
              >
                {loadingQuote ? (
                  <option value="">Loading…</option>
                ) : (
                  seats.map((seat) => (
                    <option key={seat.seat_type} value={seat.seat_type}>
                      {seatTypeLabel(seat.seat_type)}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className={styles["row"]}>
              <label className={styles["row-label"]} htmlFor="subscription">
                Billing Cycle
              </label>
              <select
                id="subscription"
                className={styles["select"]}
                value={interval}
                onChange={(e) => setInterval(e.target.value as Interval)}
              >
                {INTERVAL_ORDER.filter((i) =>
                  selectedSeat?.prices.some((p) => p.interval === i)
                ).map((i) => (
                  <option key={i} value={i}>
                    {INTERVAL_LABELS[i]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {quoteError ? (
            <p className={styles["price"]}>{quoteError}</p>
          ) : (
            <p className={styles["price"]}>{priceLabel}</p>
          )}

          <button
            className={`${styles["order-button"]} ${orderDisabled ? styles["order-button-disabled"] : ""
              }`}
            onClick={handleOrder}
            disabled={orderDisabled}
            data-gtm-id="order_now"
          >
            {outOfStock ? "Out of Stock" : submitting ? "Processing…" : orderConfig?.order_button_text || "Order Now"}
          </button>

          <div className={styles["order-footer"]}>
            {selectedSeat && selectedSeat.trial_days > 0 && !priceUnavailable && (
              trialTermsMarkdown && (
                <div className={styles["trial-terms"]}>
                  <ReactMarkdown components={sharedMarkdownComponents}>
                    {trialTermsMarkdown}
                  </ReactMarkdown>
                </div>
              )
            )}

            {structuredNotices && (
              <div className={styles["order-notices"]}>
                {structuredNotices.map((notice, i) =>
                  notice.type === "text" ? (
                    <div key={i} className={styles["order-notices-title"]}>
                      <ReactMarkdown components={sharedMarkdownComponents}>
                        {notice.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <NoticeList key={i} notice={notice} />
                  )
                )}
              </div>
            )}
          </div>
        </div>

        <div className={styles["hero-video-wrap"]}>
          <iframe
            className={styles["hero-video"]}
            src="https://www.youtube.com/embed/zCJLBdXv2fI"
            title="Firewalla Crystal demo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </section >

      {/* ---------------------------------------------------------------- */}
      {/* Run on your own hardware                                         */}
      {/* ---------------------------------------------------------------- */}
      <section className={styles["section"]}>
        <ul className={styles["hero-bullet-list"]} style={{ marginBottom: "64px" }}>
          <li>
            Dedicated <strong>Mobile</strong> &amp; <strong>MSP Web Interfaces</strong> for full
            network management
          </li>
          <li>
            Quick set up with <strong>Zero-Touch</strong> Provisioning — just flash &amp; boot
          </li>
          <li>
            Flexible <strong>3rd-party</strong> hardware deployment
          </li>
        </ul>
        <img
          src={`${IMG}/hardware-mode-icons.png`}
          alt="WAN + LAN, run on your hardware, network segmentation"
          className={styles["hardware-mode-icons-image"]}
          loading="lazy"
        />
        <h2 className={styles["section-title"]} style={{ marginBottom: "16px" }}>Hardware Requirements</h2>
        <HardwareRequirements />
      </section>

      <div className={styles["comparison-with-footnote"]}>
        <ComparisonTable
          title="Difference between Firewalla Gold Plus & Crystal SE"
          columns={goldPlusVsCrystalColumns}
          rows={goldPlusVsCrystalRows}
        />
        <p className={styles["footnote"]}>
          *System may throttle if this limit is reached
        </p>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Powered by the Firewalla Security Stack                          */}
      {/* ---------------------------------------------------------------- */}
      <section className={styles["section"]}>
        <h3 className={styles["eyebrow"]}>Powered By</h3>
        <h2 className={styles["stack-title"]}>
          <img
            src={`${IMG}/security-stack-icon.svg`}
            alt=""
            className={styles["stack-title-icon"]}
            loading="lazy"
          />
          The Firewalla Security Stack
        </h2>
        <img
          src={`${IMG}/security-stack-icons.png`}
          alt="Privacy protection, cyber security, content control, a better network, zero trust architecture"
          className={styles["full-width-image"]}
          style={{ maxWidth: "min(660px, 100%)" }}
          loading="lazy"
        />
        <img
          src={`${IMG}/security-stack-feature-grid.png`}
          alt="Firewall, Firewalla AI, Routes, Smart Queue, Geo-IP Filtering, Ad Block, Site to Site VPN, Intrusion Prevention, Safe Search, Behavior Analytics, Intrusion Detection, Bandwidth Usage, Activity Monitoring, Vulnerability Scan, VPN Server + Client"
          className={styles["full-width-image"]}
          style={{ maxWidth: "min(732px, 100%)" }}
          loading="lazy"
        />
      </section>

      <section className={`${styles["section"]} ${styles["section-wide"]}`}>
        <div className={styles["visibility-row"]}>
          <ul className={styles["bullet-list"]}>
            <li>
              <strong>Deep Insight</strong> helps you see the network and analyze every
              flow.
            </li>
            <li>
              <strong>Control</strong> your network with intrusion prevention (IPS) and
              network segmentation, adding virtual walls around your connected devices
              and locking them down.
            </li>
            <li>
              <strong>Active Protect</strong> blocks malicious sites, mitigates hacks,
              and enhances security.
            </li>
            <li>
              Integrate with Firewalla Switch + AP7, creating a{" "}
              <strong>Zero Trust Network</strong>.
            </li>
          </ul>
          <img
            src={`${IMG}/visibility-control-protect.png`}
            alt="Visibility, control, protect"
            className={styles["visibility-image"]}
            loading="lazy"
          />
        </div>
      </section>

      <section className={styles["section"]} style={{ paddingTop: "16px" }}>
        <h2 className={styles["section-title"]}>For A Better Network</h2>
        <img
          src={`${IMG}/better-network-trio.png`}
          alt="Rules for access control, Smart Queue to regulate traffic, Routes for policy-based routing"
          className={styles["full-width-image"]}
          style={{ maxWidth: 660, width: "100%" }}
          loading="lazy"
        />
      </section>

      <section className={styles["section"]} style={{ paddingTop: "16px" }}>
        <h2 className={styles["section-title"]}>Zero-Touch Provisioning</h2>
        <h3 className={styles["section-subtitle"]}>Just flash &amp; boot</h3>
        <img
          src={`${IMG}/zero-touch-provisioning-console.png`}
          alt="Firewalla provisioner flash console: downloading image, running hardware compatibility check, flashing"
          className={styles["zero-touch-console-image"]}
          style={{ maxWidth: 660, height: "auto", width: "100%" }}
          loading="lazy"
        />
      </section>

      <section className={styles["section"]} style={{ paddingTop: "16px" }}>
        <h2 className={styles["section-title"]}>Sophisticated When You Need It</h2>
        <h3 className={styles["section-subtitle"]}>
          Advanced Security and Networking Features at Your Fingertips
        </h3>
        <img
          src={`${IMG}/sophisticated-feature-tabs.gif`}
          alt="Firewall, content control, VPN server & client, ad block, intrusion prevention, network segment, bandwidth usage"
          className={styles["full-width-image"]}
          style={{ maxWidth: 660, height: "auto", width: "100%" }}
          loading="lazy"
        />
      </section>

      <section className={`${styles["section"]} ${styles["section-wide"]}`}>
        <div className={styles["msp-support-row"]}>
          <img
            src={`${IMG}/msp-web-app-support.png`}
            alt="Firewalla MSP web dashboard and mobile app"
            className={styles["msp-support-image"]}
            loading="lazy"
          />
          <div>
            <h3 className={styles["subsection-title"]}>
              Firewalla Web Interface &amp; Firewalla App Support
            </h3>
            <p>
              Firewalla Crystal can be fully managed from MSP Basic, without needing
              the Firewalla App, allowing you to manage complex network operations
              from the web interface.
            </p>
            <ul className={styles["bullet-list"]}>
              <li>24 hours (local) or up to 30 days (cloud) of Flow Storage</li>
              <li>Full network configuration support from the web interface</li>
              <li>Full support for the Firewalla App</li>
            </ul>
          </div>
        </div>
      </section>

      <div className={styles["comparison-with-footnote"]}>
        <ComparisonTable
          title="Difference between Firewalla MSP 30-Days & MSP Basic Seat"
          columns={msp30DaysVsBasicColumns}
          rows={msp30DaysVsBasicRows}
          scrollable={false}
        />
        <p className={styles["footnote"]}>
          All Firewalla MSP cloud data is stored in encrypted containers in the United
          States. Local data stays on your Firewalla.
        </p>
      </div>

      <section className={`${styles["section"]} ${styles["section-wide"]}`}>
        <div className={styles["press-row"]}>
          <img
            src={`${IMG}/press-quotes.png`}
            alt="Press quotes from PCMag, TechRadar and Digital Trends"
            className={`${styles["press-image"]} ${styles["press-quotes-image"]}`}
            loading="lazy"
          />
          <img
            src={`${IMG}/press-logos.png`}
            alt="As featured in PCMag, ZDNet, TechRadar, TechCrunch, and more"
            className={`${styles["press-image"]} ${styles["press-logos-image"]}`}
            loading="lazy"
          />
        </div>
      </section>

      <FeatureSlideshow />

      <section className={styles["section"]}>
        <h2 className={styles["section-title"]}>
          Firewalla is Running Across 100+ Countries
        </h2>
        <img
          src={`${IMG}/world-map.png`}
          alt="Map of Firewalla devices running across 100+ countries"
          className={styles["world-map-image"]}
          loading="lazy"
        />
      </section>

      <section className={styles["learn-more"]}>
        <h2 className={styles["section-title"]}>
          Want to know more? And see how it works?
        </h2>
        <div className={styles["learn-more-links"]}>
          <a
            className={styles["learn-more-link"]}
            href="https://firewalla.com/pages/reviews"
            target="_blank"
            rel="noreferrer"
          >
            <img src={`${IMG}/learn-more-product-reviews.png`} alt="" className={styles["learn-more-icon"]} loading="lazy" />
            Product Reviews
          </a>
          <a
            className={styles["learn-more-link"]}
            href={config?.crystal_install_guide_url || "https://help.firewalla.com/hc/en-us/articles/55229535959059"}
            target="_blank"
            rel="noreferrer"
          >
            <img src={`${IMG}/learn-more-installation-guide.png`} alt="" className={styles["learn-more-icon"]} loading="lazy" />
            Installation Guide
          </a>
          <a
            className={styles["learn-more-link"]}
            href="https://firewalla.com/pages/user-manual"
            target="_blank"
            rel="noreferrer"
          >
            <img src={`${IMG}/learn-more-user-manuals.png`} alt="" className={styles["learn-more-icon"]} loading="lazy" />
            User Manuals
          </a>
        </div>
        <p style={{ color: "#222", fontSize: "18px" }}>
          If you have any questions, please send us an email:{" "}
          <a href="mailto:help@firewalla.com" style={{ color: "#C8322D" }}>help@firewalla.com</a>
        </p>
      </section>
    </div >
  );
};

export default Crystal;
